/**
 * Amostragem de parametros sob restricao.
 *
 * Ordem estrita de preferencia:
 *
 *  (a) RESPOSTA PRIMEIRO (construtiva) -- sempre prefira. Nao sorteie as entradas
 *      torcendo para a resposta sair bonita: sorteie a RESPOSTA e inverta, ou
 *      pre-tabule as tuplas validas e sorteie da tabela. Rejeicao zero, vies zero.
 *
 *  (b) REJEICAO -- so para restricoes geometricas/posicionais onde inverter e
 *      inviavel ("um angulo cuja bissetriz nao seja quase paralela aos eixos").
 */

import { conta } from './pt.ts';
import type { Rng } from './rng.ts';

export class SamplingFailure extends Error {
  constructor(
    readonly label: string,
    readonly tries: number,
  ) {
    super(`amostragem '${label}' falhou após ${conta(tries, 'tentativa')}`);
    this.name = 'SamplingFailure';
  }
}

export interface SampleWhereOptions {
  readonly label: string;
  readonly maxTries?: number;
}

/**
 * Amostragem por rejeicao.
 *
 * Cada tentativa recebe um SUB-FLUXO INDEPENDENTE. Se as tentativas compartilhassem
 * um fluxo unico, o valor aceito ficaria correlacionado com quantas rejeicoes o
 * precederam -- um vies sutil e dificil de detectar. Com `fork`, o valor aceito
 * depende so de (seed, rotulo, indice da tentativa).
 *
 * Teto rigido de tentativas: uma regiao super-restrita travaria o navegador.
 */
export function sampleWhere<T>(
  rng: Rng,
  draw: (r: Rng) => T,
  ok: (x: T) => boolean,
  opts: SampleWhereOptions,
): T {
  const max = opts.maxTries ?? 200;
  for (let i = 0; i < max; i++) {
    const candidate = draw(rng.fork(`${opts.label}:${i}`));
    if (ok(candidate)) return candidate;
  }
  throw new SamplingFailure(opts.label, max);
}

/** Particiona `total` em `n` parcelas inteiras positivas, cada uma >= `min`. */
export function partitionInt(rng: Rng, total: number, n: number, min = 1): number[] {
  if (total < n * min) {
    throw new RangeError(`nao da para partir ${total} em ${n} parcelas >= ${min}`);
  }
  let restante = total - n * min;
  const parts = new Array<number>(n).fill(min);
  while (restante > 0) {
    const i = rng.int(0, n - 1);
    parts[i] = (parts[i] as number) + 1;
    restante--;
  }
  return parts;
}
